/**
 * LineDiscountEditor.tsx
 *
 * Reusable per-line discount editor: renders applied-discount badges plus an
 * "Add discount" affordance with a popover to pick a configured discount and,
 * for manual types, its dollar amount. Up to two discount slots per line.
 *
 * Extracted from PaymentProductsSection's discount UX so the packages modal can
 * apply discounts identically (same PaymentDiscount system, types 1-4). Kept
 * domain-agnostic — it takes a flat value + onChange and knows nothing about
 * products, packages, tax, or member pricing.
 */
import { useState } from 'react';
import { X, Plus } from 'lucide-react';
import type { PaymentDiscount } from '@/services/payments.service';

export interface LineDiscountValue {
  discount1Id: number | null;
  manualDiscount1: number;
  discount2Id: number | null;
  manualDiscount2: number;
}

interface LineDiscountEditorProps {
  value: LineDiscountValue;
  discounts: PaymentDiscount[];
  /** The running amount a discount applies to — used for the % preview only. */
  baseAmount: number;
  onChange: (patch: Partial<LineDiscountValue>) => void;
}

/** Manual-amount discount types (flat $ off): 1, 3, 4. Type 2 is a percentage. */
function isManualType(disc: PaymentDiscount): boolean {
  return disc.discountType === 1 || disc.discountType === 3 || disc.discountType === 4;
}

function getDiscountDisplay(disc: PaymentDiscount, manual: number): { prefix: string; name: string; suffix: string } {
  if (disc.discountType === 2) return { prefix: `(${disc.discountAmount}%)`, name: disc.title, suffix: '' };
  if (disc.discountType === 4 && manual > 0) return { prefix: '', name: disc.title, suffix: `(-$${manual.toFixed(2)})` };
  if ((disc.discountType === 1 || disc.discountType === 3) && manual > 0) return { prefix: `(-$${manual.toFixed(2)})`, name: disc.title, suffix: '' };
  return { prefix: '', name: disc.title, suffix: '' };
}

export function LineDiscountEditor({ value, discounts, baseAmount, onChange }: LineDiscountEditorProps) {
  const [popover, setPopover] = useState<{
    slot: 1 | 2 | 'new';
    discountId: number | null;
    amount: string;
  } | null>(null);

  const openPopover = (slot: 1 | 2 | 'new') => {
    if (slot === 'new') {
      setPopover({ slot: 'new', discountId: null, amount: '' });
    } else {
      const id = slot === 1 ? value.discount1Id : value.discount2Id;
      const manual = slot === 1 ? value.manualDiscount1 : value.manualDiscount2;
      setPopover({ slot, discountId: id, amount: manual > 0 ? String(manual) : '' });
    }
  };

  const applyPopover = () => {
    if (!popover || !popover.discountId) return;
    const numAmount = Number(popover.amount) || 0;
    if (popover.slot === 'new') {
      if (!value.discount1Id) {
        onChange({ discount1Id: popover.discountId, manualDiscount1: numAmount });
      } else {
        onChange({ discount2Id: popover.discountId, manualDiscount2: numAmount });
      }
    } else if (popover.slot === 1) {
      onChange({ discount1Id: popover.discountId, manualDiscount1: numAmount });
    } else {
      onChange({ discount2Id: popover.discountId, manualDiscount2: numAmount });
    }
    setPopover(null);
  };

  const removeSlot = (slot: 1 | 2) => {
    if (slot === 1) {
      onChange({ discount1Id: null, manualDiscount1: 0 });
    } else {
      onChange({ discount2Id: null, manualDiscount2: 0 });
    }
    setPopover(null);
  };

  const renderPopover = () => {
    if (!popover) return null;
    const selectedDisc = popover.discountId
      ? discounts.find((d) => d.paymentDiscountID === popover.discountId)
      : null;
    const needsAmount = selectedDisc ? isManualType(selectedDisc) : false;
    // Hide discounts already applied to the other slot so the same one can't be
    // added twice.
    const otherSlotId = popover.slot === 1 ? value.discount2Id : popover.slot === 2 ? value.discount1Id : null;
    const availableDiscounts = discounts.filter(
      (d) => d.active !== false && (popover.slot === 'new'
        ? d.paymentDiscountID !== value.discount1Id && d.paymentDiscountID !== value.discount2Id
        : d.paymentDiscountID !== otherSlotId),
    );

    return (
      <div className="absolute left-0 top-full z-50 mt-1 w-56 rounded-md border border-slate-200 bg-white p-2 shadow-lg">
        <div className="space-y-2">
          <select
            className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
            value={popover.discountId ?? ''}
            onChange={(e) => {
              const id = Number(e.target.value) || null;
              setPopover((prev) => (prev ? { ...prev, discountId: id, amount: '' } : null));
            }}
          >
            <option value="">Select discount...</option>
            {availableDiscounts.map((d) => (
              <option key={d.paymentDiscountID} value={d.paymentDiscountID}>{d.title}</option>
            ))}
          </select>
          {needsAmount && (
            <input
              type="text"
              inputMode="decimal"
              placeholder="Amount"
              value={popover.amount}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) {
                  setPopover((prev) => (prev ? { ...prev, amount: v } : null));
                }
              }}
              className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
            />
          )}
          {selectedDisc?.discountType === 2 && (
            <div className="px-1 text-[11px] text-slate-500">
              Saves ${((baseAmount * selectedDisc.discountAmount) / 100).toFixed(2)} ({selectedDisc.discountAmount}%)
            </div>
          )}
          <div className="flex items-center justify-between gap-1">
            {popover.slot !== 'new' && (
              <button
                type="button"
                className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                onClick={() => removeSlot(popover.slot as 1 | 2)}
              >
                Remove
              </button>
            )}
            <div className="ml-auto flex gap-1">
              <button
                type="button"
                className="rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
                onClick={() => setPopover(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700 disabled:opacity-50"
                disabled={!popover.discountId}
                onClick={applyPopover}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const badges: React.ReactNode[] = [];
  let showPopover = false;

  const slots: { id: number | null; manual: number; slot: 1 | 2 }[] = [
    { id: value.discount1Id, manual: value.manualDiscount1, slot: 1 },
    { id: value.discount2Id, manual: value.manualDiscount2, slot: 2 },
  ];

  for (const s of slots) {
    if (!s.id) continue;
    const disc = discounts.find((d) => d.paymentDiscountID === s.id);
    if (!disc) continue;
    if (popover?.slot === s.slot) showPopover = true;

    const display = getDiscountDisplay(disc, s.manual);
    badges.push(
      <span
        key={`d${s.slot}`}
        className="inline-flex min-w-0 max-w-full cursor-pointer items-center gap-0.5 rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[11px] text-blue-800 hover:bg-blue-100"
        onClick={() => openPopover(s.slot)}
      >
        {display.prefix && <span className="shrink-0 font-medium">{display.prefix}</span>}
        <span className="truncate">{display.name}</span>
        {display.suffix && <span className="shrink-0 font-medium">{display.suffix}</span>}
        <button
          type="button"
          className="ml-0.5 shrink-0 text-blue-500 hover:text-blue-700"
          onClick={(e) => { e.stopPropagation(); removeSlot(s.slot); }}
        >
          <X className="h-3 w-3" />
        </button>
      </span>,
    );
  }

  const appliedCount = (value.discount1Id ? 1 : 0) + (value.discount2Id ? 1 : 0);
  if (appliedCount < 2) {
    const isAdding = popover?.slot === 'new';
    if (isAdding) showPopover = true;
    badges.push(
      isAdding ? (
        <span key="add" className="inline-flex shrink-0 items-center gap-0.5 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-700">
          Applying discount…
        </span>
      ) : (
        <button
          key="add"
          type="button"
          className="inline-flex shrink-0 items-center gap-0.5 rounded border border-dashed border-slate-300 px-1.5 py-0.5 text-[11px] text-slate-500 hover:border-slate-400 hover:text-slate-700"
          onClick={() => openPopover('new')}
        >
          <Plus className="h-3 w-3" /> Discount
        </button>
      ),
    );
  }

  return (
    <div className="relative min-w-0">
      <div className="flex flex-wrap items-center gap-1">
        {badges.length > 0 ? badges : <span className="text-xs text-slate-400">—</span>}
      </div>
      {showPopover && renderPopover()}
    </div>
  );
}
